import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Eye, EyeOff } from "lucide-react";

const searchSchema = z.object({
  estimateId: z.string().optional(),
});

export const Route = createFileRoute("/_app/contracts/new")({
  component: NewContractPage,
  validateSearch: searchSchema,
});

type ClientOpt = { id: string; name: string };
type EstimateOpt = {
  id: string;
  number: string;
  title: string;
  client_id: string;
  total: number;
  description: string | null;
  currency: string;
};

function NewContractPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, company } = useAuth();
  const { estimateId: prefillEstimateId } = useSearch({ from: "/_app/contracts/new" });

  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [estimates, setEstimates] = useState<EstimateOpt[]>([]);
  const [usedEstimateIds, setUsedEstimateIds] = useState<Set<string>>(new Set());
  const [clientId, setClientId] = useState<string>("");
  const [estimateId, setEstimateId] = useState<string>("none");
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState("");
  const [terms, setTerms] = useState(t("pages.contracts.form.termsDefault"));
  const [amount, setAmount] = useState<number>(0);
  const [retainagePercent, setRetainagePercent] = useState<number>(0);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [companySigner, setCompanySigner] = useState<string>("");
  const [inspections, setInspections] = useState("");
  const [insuranceLiability, setInsuranceLiability] = useState("");
  const [materialsWorkmanship, setMaterialsWorkmanship] = useState("");
  const [cleanup, setCleanup] = useState("");
  const [completionTerms, setCompletionTerms] = useState("");
  const [agreementText, setAgreementText] = useState("");
  const [showClauses, setShowClauses] = useState(false);
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const toggleSection = (key: string) =>
    setHiddenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderSectionLabel = (key: string, label: string) => {
    const hidden = hiddenSections.has(key);
    return (
      <div className="flex items-center justify-between gap-2">
        <Label className={hidden ? "text-muted-foreground line-through" : ""}>{label}</Label>
        <button
          type="button"
          onClick={() => toggleSection(key)}
          className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors ${
            hidden
              ? "text-amber-600 hover:text-amber-700"
              : "text-muted-foreground hover:text-foreground"
          }`}
          title={
            hidden
              ? t("pages.contracts.detail.sectionShowTitle")
              : t("pages.contracts.detail.sectionHideTitle")
          }
        >
          {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {hidden
            ? t("pages.contracts.detail.sectionHidden")
            : t("pages.contracts.detail.sectionVisible")}
        </button>
      </div>
    );
  };

  useEffect(() => {
    (async () => {
      const [{ data: cl }, { data: est }, { data: existingContracts }] = await Promise.all([
        supabase.from("clients").select("id, name").eq("is_active", true).order("name"),
        supabase
          .from("estimates")
          .select("id, number, title, client_id, total, description, currency")
          .eq("status", "approved")
          .order("created_at", { ascending: false }),
        supabase.from("contracts").select("estimate_id").not("estimate_id", "is", null),
      ]);
      setClients((cl ?? []) as ClientOpt[]);
      setEstimates((est ?? []) as EstimateOpt[]);
      setUsedEstimateIds(
        new Set(
          ((existingContracts ?? []) as { estimate_id: string | null }[])
            .map((c) => c.estimate_id)
            .filter((id): id is string => !!id),
        ),
      );
    })();
  }, []);

  useEffect(() => {
    if (profile?.full_name && !companySigner) setCompanySigner(profile.full_name);
  }, [profile?.full_name, companySigner]);

  // Prefill from estimate if passed via URL
  useEffect(() => {
    if (!prefillEstimateId || estimates.length === 0) return;
    const est = estimates.find((e) => e.id === prefillEstimateId);
    if (est) applyEstimate(est);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillEstimateId, estimates]);

  const extractDescription = (text: string): string => {
    // Remove markdown headings like "## Escopo", "## Scope", "# Description" etc.
    // Keep only the body text after the first heading (or all text if no heading).
    const lines = text.split("\n");
    const out: string[] = [];
    for (const line of lines) {
      if (/^\s*#{1,6}\s+/.test(line)) continue; // skip heading lines
      out.push(line);
    }
    return out.join("\n").trim();
  };

  const applyEstimate = (est: EstimateOpt) => {
    setEstimateId(est.id);
    setClientId(est.client_id);
    if (!title) setTitle(est.title);
    if (!scope && est.description) setScope(extractDescription(est.description));
    if (!amount) setAmount(Number(est.total) || 0);
  };

  const onEstimateChange = (val: string) => {
    if (val === "none") {
      setEstimateId("none");
      return;
    }
    const est = estimates.find((e) => e.id === val);
    if (est) applyEstimate(est);
  };

  const currency = company?.currency || "USD";

  const save = async (status: "draft" | "sent") => {
    if (!clientId) return toast.error(t("pages.contracts.form.errorClient"));
    if (estimateId === "none") {
      return toast.error(t("pages.contracts.form.errorEstimateRequired"));
    }
    if (!title.trim()) return toast.error(t("pages.contracts.form.errorTitle"));
    if (!company?.id || !profile?.id) return toast.error(t("pages.contracts.form.errorContext"));

    setSaving(true);
    try {
      const linkedEstimateId = estimateId !== "none" ? estimateId : null;

      // Prevent duplicate: an estimate can only become a single contract
      if (linkedEstimateId) {
        const { data: existing, error: dupErr } = await supabase
          .from("contracts")
          .select("id")
          .eq("estimate_id", linkedEstimateId)
          .limit(1)
          .maybeSingle();
        if (dupErr) throw dupErr;
        if (existing) {
          setSaving(false);
          toast.error(t("pages.contracts.form.errorDuplicateEstimate"));
          return;
        }
      }

      const { data, error } = await supabase
        .from("contracts")
        .insert({
          company_id: company.id,
          client_id: clientId,
          estimate_id: linkedEstimateId,
          created_by: profile.id,
          title: title.trim(),
          scope: scope.trim() || null,
          terms: terms.trim() || null,
          inspections: inspections.trim() || null,
          insurance_liability: insuranceLiability.trim() || null,
          materials_workmanship: materialsWorkmanship.trim() || null,
          cleanup: cleanup.trim() || null,
          completion_terms: completionTerms.trim() || null,
          agreement_text: agreementText.trim() || null,
          hidden_sections: [...hiddenSections],
          amount,
          retainage_percent: retainagePercent,
          currency,
          start_date: startDate || null,
          end_date: endDate || null,
          company_signature_name: companySigner.trim() || null,
          status,
          number: "",
        })
        .select("id")
        .single();
      if (error || !data) throw error ?? new Error("Failed to create");

      toast.success(
        status === "draft"
          ? t("pages.contracts.form.draftSaved")
          : t("pages.contracts.form.contractSent"),
      );
      navigate({ to: "/contracts/$contractId", params: { contractId: data.id } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not save";
      toast.error(t("pages.contracts.form.saveFailed"), { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/contracts" })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("pages.contracts.form.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("pages.contracts.form.subtitle")}</p>
        </div>
      </div>

      <div className="grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t("pages.contracts.form.client")}</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger>
              <SelectValue placeholder={t("pages.contracts.form.selectClient")} />
            </SelectTrigger>
            <SelectContent>
              {clients.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {t("pages.contracts.form.noClients")}
                </div>
              ) : (
                clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>{t("pages.contracts.form.fromEstimate")}</Label>
          <Select value={estimateId} onValueChange={onEstimateChange}>
            <SelectTrigger>
              <SelectValue placeholder={t("pages.contracts.form.none")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("pages.contracts.form.noneScratch")}</SelectItem>
              {estimates
                .filter((e) => !usedEstimateIds.has(e.id) || e.id === prefillEstimateId)
                .filter((e) => !clientId || e.client_id === clientId)
                .map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.number} — {e.title}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label>{t("pages.contracts.form.contractTitle")}</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("pages.contracts.form.titlePlaceholder")}
          />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          {renderSectionLabel("scope", t("pages.contracts.form.scope"))}
          <Textarea
            rows={4}
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder={t("pages.contracts.form.scopePlaceholder")}
            className={hiddenSections.has("scope") ? "opacity-60" : ""}
          />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          {renderSectionLabel("payment", t("pages.contracts.form.terms"))}
          <Textarea
            rows={5}
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            className={hiddenSections.has("payment") ? "opacity-60" : ""}
          />
          <p className="text-xs text-muted-foreground">
            {t("pages.contracts.detail.paymentToggleHint")}
          </p>
        </div>

        <div className="space-y-1.5">
          {renderSectionLabel("payment", t("pages.contracts.form.amount", { currency }))}
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            className={hiddenSections.has("payment") ? "opacity-60" : ""}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.contracts.form.retainage")}</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={retainagePercent}
            onChange={(e) => setRetainagePercent(Number(e.target.value) || 0)}
          />
          <p className="text-xs text-muted-foreground">{t("pages.contracts.form.retainageHint")}</p>
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.contracts.form.companySigner")}</Label>
          <Input
            value={companySigner}
            onChange={(e) => setCompanySigner(e.target.value)}
            placeholder={t("pages.contracts.form.companySignerPlaceholder")}
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t("pages.contracts.form.startDate")}</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.contracts.form.endDate")}</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      <button
        type="button"
        className="flex w-full items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setShowClauses(!showClauses)}
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform ${showClauses ? "rotate-180" : ""}`}
        />
        {t("pages.contracts.detail.clauseHint")}
      </button>

      {showClauses && (
        <div className="grid gap-4 rounded-lg border border-border bg-card p-5">
          <div className="rounded-md border border-dashed border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            {t("pages.contracts.detail.hideHint")}
          </div>
          <div className="space-y-1.5">
            {renderSectionLabel("inspections", t("pages.contracts.detail.editInspectionsLabel"))}
            <Textarea
              value={inspections}
              onChange={(e) => setInspections(e.target.value)}
              rows={3}
              className={hiddenSections.has("inspections") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5">
            {renderSectionLabel(
              "insurance_liability",
              t("pages.contracts.detail.editInsuranceLabel"),
            )}
            <Textarea
              value={insuranceLiability}
              onChange={(e) => setInsuranceLiability(e.target.value)}
              rows={3}
              className={hiddenSections.has("insurance_liability") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5">
            {renderSectionLabel(
              "materials_workmanship",
              t("pages.contracts.detail.editMaterialsLabel"),
            )}
            <Textarea
              value={materialsWorkmanship}
              onChange={(e) => setMaterialsWorkmanship(e.target.value)}
              rows={3}
              className={hiddenSections.has("materials_workmanship") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5">
            {renderSectionLabel("cleanup", t("pages.contracts.detail.editCleanupLabel"))}
            <Textarea
              value={cleanup}
              onChange={(e) => setCleanup(e.target.value)}
              rows={2}
              className={hiddenSections.has("cleanup") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5">
            {renderSectionLabel("completion", t("pages.contracts.detail.editCompletionLabel"))}
            <Textarea
              value={completionTerms}
              onChange={(e) => setCompletionTerms(e.target.value)}
              rows={2}
              className={hiddenSections.has("completion") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5">
            {renderSectionLabel("agreement", t("pages.contracts.detail.editAgreementLabel"))}
            <Textarea
              value={agreementText}
              onChange={(e) => setAgreementText(e.target.value)}
              rows={2}
              className={hiddenSections.has("agreement") ? "opacity-60" : ""}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={() => save("draft")} disabled={saving}>
          {t("pages.contracts.form.saveDraft")}
        </Button>
        <Button onClick={() => save("sent")} disabled={saving}>
          {saving ? t("pages.contracts.form.saving") : t("pages.contracts.form.saveSent")}
        </Button>
      </div>
    </div>
  );
}
